import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Box, Flex } from '../Rebass';
import Heading from '../Heading';
import Text from '../Text';
import theme from '../../theme';
import Pattern, { LAYOUT_TYPES, PatternProps } from '../Pattern';
import { motion } from '../Motion';
import useHeroParallax from '../../hooks/useHeroParallax';
import { SxStyleProp } from '../Rebass/types';

interface HeroProps {
  /** The main text to appear over the image*/
  headline?: string;
  /** An optional description below the headline*/
  description?: string;
  /** An optional small caption*/
  caption?: string;
  /** Set the color of the text*/
  color?: string;
  /** Setting the lodge will change the button and decorative elements*/
  // TODO: Set the lodges as constants to limit the options
  lodge?: string;
  /** Background image of the Hero*/
  image?: React.ReactNode;
  /** Should the pattern show? */
  showPattern?: boolean;
  /** Pattern's path etc */
  patternProps?: PatternProps;
  /** Render prop to add a button to the Hero. Used for functional items like "Play Video" etc*/
  renderButton?: (colorScheme: string) => React.ReactNode;
  /** The color to use when a color scheme has been applied (as for Lodges) */
  colorScheme?: string;
  captionSx?: SxStyleProp;
  headlineSx?: SxStyleProp;
  heroType?: string;
}

const StyledHero = styled(Flex)`
  width: 100%;
  height: 100vh;
  position: relative;
`;

const ImageContainer = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;
const HeroImage = motion.custom(styled(Box)`
  width: 100%;
  height: 100%;
`);

const AdditionalContent = styled(Box)`
  position: absolute;
  display: grid;
  display: -ms-grid;
  grid-template-rows: 50px;
  -ms-grid-rows: 50px;
  // prettier-ignore
  -ms-grid-columns: (33%)[3];
  left: 0;
  bottom: 0;
  width: 100%;

  @media (max-width: ${theme.breakpoints[1]}) {
    justify-items: center;
  }
`;

export const ProgressContainer = styled(Box)`
  position: absolute;
  right: ${theme.space[5]}px;
  bottom: 0;
`;

const animate = keyframes`
  0% {
      top: -50%;
  }
  100% {
      top: 100%;
  }
  0% {
      top: -50%;
  }
}`;

export const ProgressLine = styled(Box)<{ color?: string }>`
  width: 1px;
  position: relative;
  height: 100px;
  overflow: hidden;

  &:before {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    border-left: 1px solid ${theme.colors.baseWhite};
  }

  &:after {
    content: '';
    width: 100%;
    height: 50%;
    position: absolute;
    border-left: 1px solid
      ${({ color }) => (color ? color : theme.colors.brandBrown)};
    animation: ${animate} 1.75s ease-in infinite;
  }
`;

const Headline = {
  Container: styled(Box)`
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
  `,
  Inner: styled(Box)`
    position: absolute;
    max-width: 80%;
    @media (max-width: ${theme.breakpoints[3]}) {
      min-width: 90%;
    }
    ${(props) =>
      props.heroType === 'SPVC'
        ? `bottom: 10%;
          left: 10%;
          @media (min-width: ${theme.breakpoints[1]}) {
            max-width: 40%;
          }`
        : `top: 50%;
   left: 50%;
   transform: translate(-50%, -50%);`}
  `,
};

const Cell = styled(Box)`
  -ms-grid-column: ${({ column }) => column};
  -ms-grid-row: 1;
`;

const Caption = motion.custom(Box);

const DescriptionContainer = styled(Box)`
  position: absolute;
  max-width: 80%;
  ${(props) =>
    props.heroType === 'SPVC'
      ? `bottom: 2%;
        left: 10%;
        @media (min-width: ${theme.breakpoints[1]}) {
          max-width: 40%;
        }`
      : `top: 62%;
        left: 50%;
        transform: translate(-50%, -50%);`}
`;

const Hero = forwardRef<HTMLDivElement, HeroProps>(
  (
    {
      headline,
      description,
      caption,
      color = theme.colors.textWhite,
      image,
      showPattern,
      patternProps,
      renderButton = () => null,
      colorScheme,
      captionSx,
      headlineSx,
      heroType,
      ...rest
    }: HeroProps,
    ref,
  ) => {
    const { headlineStyle, backgroundStyle, captionStyle } = useHeroParallax();
    return (
      <Box>
        <StyledHero
          ref={ref}
          justifyContent="center"
          alignItems="center"
          {...rest}
        >
          <ImageContainer>
            <HeroImage
              role="img"
              title={caption || headline || null}
              // @ts-ignore
              style={backgroundStyle}
            >
              {image}
            </HeroImage>
          </ImageContainer>
          {headline && (
            <Headline.Container>
              <Headline.Inner heroType={heroType}>
                {/* @ts-ignore */}
                <motion.div
                  style={{
                    ...headlineStyle,
                    willChange: 'transform, opacity, letter-spacing',
                  }}
                >
                  <Heading
                    as="h1"
                    size="hero"
                    color={color}
                    sx={{ ...headlineSx }}
                  >
                    {headline}
                  </Heading>
                </motion.div>
              </Headline.Inner>
            </Headline.Container>
          )}
          {description && (
            <DescriptionContainer heroType={heroType}>
              {/* @ts-ignore */}
              <motion.div
                style={{
                  ...headlineStyle,
                  willChange: 'transform, opacity, letter-spacing',
                }}
              >
                <Heading size="h3" color={color} sx={headlineSx}>
                  {description}
                </Heading>
              </motion.div>
            </DescriptionContainer>
          )}
          <AdditionalContent
            sx={{ gridTemplateColumns: ['100%', '20% 60% 20%'] }}
            p={[2]}
          >
            {renderButton && (
              <Cell column={1} sx={{ display: ['none', 'flex'] }}>
                <Flex justifyContent="flex-start" alignItems="flex-end">
                  {renderButton && renderButton(color)}
                </Flex>
              </Cell>
            )}
            {caption && (
              <Cell
                sx={{ display: 'flex' }}
                justifyContent="center"
                alignItems="flex-end"
                textAlign="center"
                column={2}
              >
                <Caption
                  sx={{ maxWidth: ['70%', 'initial'] }}
                  style={captionStyle}
                >
                  <Text
                    size="caption"
                    color={color}
                    textAlign="center"
                    fontFamily={'variable'}
                    sx={{ ...captionSx }}
                  >
                    {caption}
                  </Text>
                </Caption>
              </Cell>
            )}
          </AdditionalContent>
          <ProgressContainer>
            <ProgressLine color={colorScheme} />
          </ProgressContainer>
          {showPattern && (
            <Pattern layoutType={LAYOUT_TYPES.HERO} {...patternProps} />
          )}
        </StyledHero>
      </Box>
    );
  },
);

export default Hero;
